import React from "react";
import { X } from "lucide-react";
import { PIZZA_ICONS } from "../constants/pizzaConstants";
import { Tamanho } from "../../../services/tamanhoService";
import { usePizzaPreferences } from "../hooks/usePizzaPreferences";

interface PizzaPreferencesModalProps {
  tamanhos: Tamanho[];
  userId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

const PizzaPreferencesModal: React.FC<PizzaPreferencesModalProps> = ({
  tamanhos,
  userId,
  isOpen,
  onClose,
}) => {
  const {
    visiblePizzaIds,
    togglePizzaVisibility,
    setAllVisible,
    isPizzaVisible,
  } = usePizzaPreferences(userId);

  const handleSelectAll = () => {
    setAllVisible(tamanhos.map((t) => t.id));
  };

  const handleDeselectAll = () => {
    setAllVisible([]);
  };

  const handleToggle = (pizzaId: number) => {
    if (visiblePizzaIds === null) {
      const allIds = tamanhos.map((t) => t.id);
      const newSet = new Set(allIds);

      if (newSet.has(pizzaId)) {
        newSet.delete(pizzaId);
      }
      setAllVisible(Array.from(newSet));
    } else {
      togglePizzaVisibility(pizzaId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-neutral-400 hover:text-neutral-600"
        >
          <X size={24} />
        </button>

        <h3 className="mb-4 text-2xl font-bold text-primary">
          Configurar Pizzas Visíveis
        </h3>
        <p className="mb-6 text-sm text-neutral-600">
          Selecione quais pizzas deseja exibir nos cards
        </p>

        <div className="mb-4 flex gap-2">
          <button
            onClick={handleSelectAll}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
          >
            Selecionar Todas
          </button>
          <button
            onClick={handleDeselectAll}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            Desmarcar Todas
          </button>
        </div>

        <div className="max-h-96 space-y-2 overflow-y-auto">
          {tamanhos.map((tamanho) => {
            const isVisible = isPizzaVisible(tamanho.id);
            return (
              <label
                key={tamanho.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-colors hover:bg-neutral-50 ${
                  isVisible
                    ? "border-primary bg-error-light"
                    : "border-neutral-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => handleToggle(tamanho.id)}
                  className="h-5 w-5 cursor-pointer rounded border-neutral-300 text-primary focus:ring-primary"
                />
                <div className="flex flex-1 items-center gap-3">
                  <span className="text-2xl">
                    {PIZZA_ICONS[tamanho.nome] ?? "🍕"}
                  </span>
                  <span className="font-medium text-neutral-800">
                    {tamanho.nome}
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-dark"
        >
          Salvar
        </button>
      </div>
    </div>
  );
};

export default PizzaPreferencesModal;
